import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, take } from 'rxjs';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import {
  getAccountPreferences,
  getUserLoadStatus,
  REPORT_ACCESS_ONLY_PREFERENCE_KEY,
  REPORT_ACCESS_RESTRICTION_FEATURE_TOGGLE
} from '@capital-access/common/user';
import { LoadStatus } from '@capital-access/common/utils';

@Injectable({ providedIn: 'root' })
export class ReportAccessOnlyGuard implements CanActivate, CanActivateChild {
  private static readonly ALLOWED_ROUTES = ['/reporting', '/user', '/error', '/forbidden', '/session-expire'];

  constructor(private store: Store, private router: Router) {}

  canActivate(
    _route: ActivatedRouteSnapshot | null = null,
    state: RouterStateSnapshot | null = null
  ): Observable<boolean> {
    const userStatus$ = this.store.select(getUserLoadStatus);
    const preferences$ = this.store.select(getAccountPreferences);
    const featureToggle$ = this.store.select(getFeatureToggle(REPORT_ACCESS_RESTRICTION_FEATURE_TOGGLE));

    return combineLatest([userStatus$, preferences$, featureToggle$]).pipe(
      map(([status, preferences, isFeatureEnabled]) => {
        if (status !== LoadStatus.Loaded || !isFeatureEnabled || !preferences || !Array.isArray(preferences)) {
          return true;
        }

        const currentUrl = state?.url || this.router.url;
        if (this.isAllowedRoute(currentUrl)) {
          return true;
        }

        // Check if user has report-access-only restriction
        const hasReportAccessOnly = preferences.some(
          pref => pref.key === REPORT_ACCESS_ONLY_PREFERENCE_KEY && pref.value === 'true'
        );

        if (hasReportAccessOnly) {
          this.router.navigate(['/reporting']);
        }
        return true;
      }),
      take(1)
    );
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.canActivate(childRoute, state);
  }

  private isAllowedRoute(url: string): boolean {
    return ReportAccessOnlyGuard.ALLOWED_ROUTES.some(route => url.startsWith(route));
  }
}
